import './styles/ClientRetrievalIssuesStyle.css';
import React, { useEffect, useState } from 'react';
import {
  NavigationButton,
  ToastType,
  TopNav,
  TopNavToastMessage,
} from '../common';
import { languageMap } from '../../maps/languageMap';
import { useActions, useTypedSelector } from '../../hooks';
import { APIResponseStatusType, navigationPages } from '../../models/types';
import { useHistory } from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import { apiErrorHandler, apiGET, apiPOST } from '../../restApi';
import { Incident, LockerPosition } from '../../models/microservices';
import {
  IncidentRequestGET,
  IncidentRequestPOST,
  IncidentResponseGET,
  IncidentResponsePOST,
} from '../../models/cruds';

const ISSUE_CODE_DOES_NOT_OPEN = 'B';
const ISSUE_CODE_DOES_NOT_CLOSE = 'C';
const ISSUE_COMPLETE_LIST = [
  ISSUE_CODE_DOES_NOT_OPEN,
  ISSUE_CODE_DOES_NOT_CLOSE,
];
const ISSUE_DELIVERED_LIST = [ISSUE_CODE_DOES_NOT_CLOSE];
const VALIDATION_ERROR = 'Error en la consulta';
const SELECT_INCIDENT = 'Por favor selecciona una incidencia';
const ISSUE_CODE_DOES_NOT_OPEN_FEEDBACK =
  'Por favor acércate a uno de nuestros asesores para recibir asistencia. Pulsa Terminar o Tengo otra guía si tienes otro paquete en el locker.';
const ISSUE_CODE_DOES_NOT_CLOSE_FEEDBACK =
  '¡Muchas gracias por tu retroalimentación, se ha registrado la incidencia para resolverla cuanto antes! Pulsa Terminar o Tengo otra guía si tienes otro paquete en el locker.';

const ClientRetrievalIssues: React.FC = () => {
  let history = useHistory();
  const [loading, setLoading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [showFinishButton, setShowFinishButton] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [selectedIncident, setSelectedIncident] = useState<Incident>(
    {} as Incident
  );
  const [incidentList, setIncidentList] = useState<Incident[]>([]);
  const language = useTypedSelector(({ session }) => {
    return session.language;
  });
  const token = useTypedSelector(({ session }) => {
    return session.dhlApiToken;
  });
  const agentToken = useTypedSelector(({ session }) => {
    return session.dhlAgentToken;
  });
  const currentLockerPosition = useTypedSelector(({ locker }) => {
    return locker.currentLockerPosition;
  });
  useEffect(() => {
    const getIncidents = async (): Promise<boolean> => {
      setLoading(true);
      let request = new IncidentRequestGET();
      let response: IncidentResponseGET = await apiGET(
        request.buildURL(),
        request.generateConfig(token)
      );
      let errorMsg = '';
      try {
        if (response.status === APIResponseStatusType.SUCCESS) {
          if (response.data.incidents) {
            let filteredList = response.data.incidents.filter((incident) =>
              ISSUE_COMPLETE_LIST.includes(incident.code)
            );
            if (currentLockerPosition.retrieved) {
              filteredList = response.data.incidents.filter((incident) =>
                ISSUE_DELIVERED_LIST.includes(incident.code)
              );
            }
            setIncidentList(filteredList);
            setLoading(false);
            return true;
          } else {
            //TODO check for unauth and refresh token
            errorMsg = 'Unauthorized';
          }
        } else {
          errorMsg = apiErrorHandler(response);
        }
      } catch (err: any) {
        errorMsg = err.toString();
      }
      console.log(errorMsg);
      setFeedbackMessage(VALIDATION_ERROR);
      setLoading(false);
      return false;
    };
    getIncidents();
  }, [token, currentLockerPosition]);
  const { updateIsMultiDelivery, updateCurrentLockerPosition } = useActions();

  const postIssueAsync = async (): Promise<boolean> => {
    let packageCandidateId = currentLockerPosition.packageCandidate?.id ?? 0;
    let lockerPositionId = currentLockerPosition.id;
    let request = new IncidentRequestPOST();
    let response: IncidentResponsePOST = await apiPOST(
      request.buildURL(),
      request.generateContent(
        packageCandidateId,
        lockerPositionId,
        selectedIncident.id
      ),
      request.generateConfig(agentToken)
    );
    let errorMsg = '';
    try {
      if (response.status === APIResponseStatusType.SUCCESS) {
        return true;
      } else {
        errorMsg = apiErrorHandler(response);
      }
    } catch (err: any) {
      errorMsg = err.toString();
    }
    console.log(errorMsg);
    setFeedbackMessage(VALIDATION_ERROR);
    setLoading(false);
    return false;
  };
  const confirmIssue = async () => {
    if (selectedIncident.code === '') {
      setFeedbackMessage(SELECT_INCIDENT);
      return;
    }
    switch (selectedIncident.code) {
      case ISSUE_CODE_DOES_NOT_OPEN:
        if (await postIssueAsync()) {
          setShowFinishButton(true);
          setInstructions(ISSUE_CODE_DOES_NOT_OPEN_FEEDBACK);
        }
        setLoading(false);
        return;
      case ISSUE_CODE_DOES_NOT_CLOSE:
        if (await postIssueAsync()) {
          setShowFinishButton(true);
          setInstructions(ISSUE_CODE_DOES_NOT_CLOSE_FEEDBACK);
        }
        setLoading(false);
        return;
      default:
        break;
    }
  };
  const renderContent = () => {
    if (loading) {
      return (
        <div className="remove-issues-issue-list-container">
          <div className="spinner-container">
            <Spinner className="spinner" animation="grow" variant="danger" />
          </div>
        </div>
      );
    }
    if (instructions !== '') {
      return (
        <div className="remove-issues-issue-list-container">
          <p className="remove-issues-instructions">{instructions}</p>
        </div>
      );
    }
    return (
      <div className="remove-issues-issue-list-container">
        {incidentList.map((incident) => {
          return (
            <div
              key={incident.id}
              className={`remove-issues-issue-container ${
                incident.id === selectedIncident.id ? 'selected' : ''
              } `}
              onClick={() => setSelectedIncident(incident)}
            >
              <p className="remove-issues-issue-name">{incident.name}</p>
              <div
                className={`remove-issues-issue-indicator ${
                  incident.id === selectedIncident.id ? 'selected' : ''
                } `}
              />
            </div>
          );
        })}
      </div>
    );
  };
  const handleMultiDelivery = () => {
    updateIsMultiDelivery(true);
    updateCurrentLockerPosition({} as LockerPosition);
    history.replace(`/${navigationPages.CLIENT_RETRIEVAL_SCAN_AWB}`);
  };
  const renderMiddleButton = () => {
    if (loading) {
      return null;
    }
    if (!showFinishButton) {
      return (
        <NavigationButton
          buttonContent={
            languageMap.client_retrieval_issues.confirm_button[language]
          }
          buttonClass="navigation-button confirm-button"
          onClickButton={() => {
            confirmIssue();
          }}
        />
      );
    } else {
      return (
        <NavigationButton
          buttonContent={
            languageMap.client_retrieval_issues.other_piece_button[language]
          }
          buttonClass="navigation-button confirm-button"
          onClickButton={() => {
            handleMultiDelivery();
          }}
        />
      );
    }
  };
  const renderFinishButton = () => {
    if (!loading && showFinishButton) {
      return (
        <NavigationButton
          buttonContent={
            languageMap.client_retrieval_issues.finish_button[language]
          }
          buttonClass="navigation-button confirm-button"
          onClickButton={() => {
            history.replace(`/${navigationPages.THANK_YOU}`);
          }}
        />
      );
    }
  };
  return (
    <div id="clientRetrievalIssues" className="main-container">
      <TopNav dataCyPrefix="scan-awb" />
      <div className="content-container">
        <div className="main-content-container fullpage">
          <TopNavToastMessage
            toastText={feedbackMessage}
            toastType={ToastType.ERROR}
            resetMessage={() => setFeedbackMessage('')}
          />
          <div className="title-content-container">
            <p className="header nopadding nomargin">
              {languageMap.client_retrieval_issues.title[language]}
            </p>
          </div>
          <div className="data-content-container">{renderContent()}</div>
          <div className="footer-container">
            <div className="footer-button-container">
              <NavigationButton
                buttonContent={languageMap.remove_issues.back_button[language]}
                buttonClass="navigation-button cancel-button"
                onClickButton={() => {
                  history.replace(`/${navigationPages.REMOVE_PIECE}`);
                }}
              />
            </div>
            <div className="footer-button-container">
              {renderMiddleButton()}
            </div>
            <div className="footer-button-container">
              {renderFinishButton()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ClientRetrievalIssues };
